import React, { forwardRef, memo } from 'react';

import type {
  AccessibilityRole,
  TextProps as RNTextProps,
  StyleProp,
  TextProps,
  TextStyle,
} from 'react-native';
import { Text as RNText, StyleSheet } from 'react-native';

import { primitive } from 'styles/constants';
import { HeaderAccessibilityLevel, fontConfig, fontSizes } from 'styles/constants/fonts';
import { textVariants } from 'styles/constants/fonts';

import type { SpacingProps } from '../../utils/element-spacings';
import { getStylesWithSpacing } from '../../utils/element-spacings';

import { omitMaxFontSizeMultiplierOnWeb } from './platform-text-props';

export interface TypographyProps extends Omit<RNTextProps, 'style'>, SpacingProps {
  variant?: TextVariant;
  style?: StyleProp<TextStyle>;
  children?: React.ReactNode;
  color?: string;
  bold?: boolean;
  fontFamily?: 'heading' | 'body';
  fontSize?: number | FontSizeToken;
  /**
   * Line height in dp (number), a design-system token (`'sm' | 'md' | ...`), or a custom
   * em-multiplier string (`'1.6em'`). Tokens and em-strings resolve against the effective
   * fontSize; raw numbers pass through unchanged (matching RN's `TextStyle.lineHeight`).
   */
  lineHeight?: number | LineHeightToken | string;
  /**
   * Line height as an em-multiplier of the resolved fontSize. Equivalent to passing an
   * em-string to `lineHeight` (e.g. `lineHeightMultiplier={1.6}` ↔ `lineHeight="1.6em"`).
   * Ignored if `lineHeight` is also set.
   */
  lineHeightMultiplier?: number;
  textAlign?: TextStyle['textAlign'];
  /**
   * Makes the rendered Text a tab-stop (web) / D-pad focus target (native).
   * Not part of RN core's `TextProps` typing but supported by RN-web at runtime; surfaced here
   * so headings can own their focus instead of being wrapped in a focusable parent (which
   * swallows the heading role for screen readers).
   */
  focusable?: boolean;
  /**
   * Draws the underline as a bottom border instead of `textDecorationLine`.
   *
   * iOS's text decoration renderer skips the rule wherever a glyph outline crosses it, so
   * round letters (e, c, o) whose bowls overshoot the baseline punch visible gaps in the
   * underline. A border is drawn independently of the glyphs and stays solid.
   *
   * Opt-in rather than the default for `textDecorationLine`, because a border sits at the
   * bottom of the *line box* rather than just under the baseline: switching existing callers
   * over would drop their underline by however much leading the variant carries. Pair it with
   * a `lineHeight` close to the fontSize to bring the rule back up under the text.
   */
  underline?: boolean;
}
/**
 * IN PROGRESS: Drop in NativeBase replacement.
 * A styled Text component using React Native's Text with design system variants.
 *
 * @example
 * ```tsx
 * <StyledText variant="headerOne" color="green" bold>
 *   Bold Green Header Text with correct accessibilityRole
 * </StyledText>
 * ```
 */
export const Typography = forwardRef<RNText, TypographyProps>(
  (
    {
      variant = 'copyOne',
      style,
      color,
      bold,
      fontSize,
      lineHeight,
      lineHeightMultiplier,
      textAlign,
      fontFamily,
      underline,
      ...restProps
    },
    ref
  ) => {
    // map convenience properties to real styles
    const boldStyle: TextStyle | undefined = bold
      ? fontFamilyMap[textVariants[variant]?.fontFamily ?? 'body']?.bold
      : undefined;

    const lineHeightStyle = resolveLineHeightStyle(
      lineHeight,
      lineHeightMultiplier,
      fontSize,
      variant
    );

    // final calculated style, passed in style wins
    const sheets = stylesheetStyles as Record<string, TextStyle>;
    const finalStyle = getStylesWithSpacing(
      [
        sheets.base,
        sheets[variant],
        boldStyle,
        fontSize
          ? typeof fontSize === 'number'
            ? { fontSize }
            : sheets[fontSize as TextVariant]
          : undefined,
        fontFamily
          ? (fontFamilyMap[fontFamily]?.[bold ? 'bold' : 'regular'] as TextStyle | undefined)
          : undefined,
        color ? { color } : undefined,
        textAlign ? ({ textAlign } as TextStyle) : undefined,
        lineHeightStyle,
        // Placed after `color` so the rule inherits it, and before the caller's
        // `style` so an explicit borderBottom* still wins.
        underline ? underlineStyle(color) : undefined,
      ],
      style,
      restProps
    );

    const finalProps = omitMaxFontSizeMultiplierOnWeb(addVariantProps(variant, { ...restProps }));

    return (
      <RNText ref={ref} style={finalStyle} {...finalProps}>
        {restProps.children}
      </RNText>
    );
  }
);
Typography.displayName = 'Typography';

/**
 * Creates a Typography-based component pre-configured with default props.
 * Caller-provided `style` is merged on top of the helper's default `style`
 * (callers can still override individual style keys).
 *
 * @example
 *   const Title = withTypography({ variant: 'headerOne', bold: true });
 *   <Title>Hello</Title>
 */
export function withTypography(defaultProps: Partial<TypographyProps>) {
  const Component = (props: TypographyProps) =>
    React.createElement(Typography, {
      ...defaultProps,
      ...props,
      style: [defaultProps.style, props.style],
    });
  Component.displayName = 'withTypography';
  return memo(Component);
}

type TextVariant = keyof typeof textVariants;
type FontSizeToken = keyof typeof fontSizes;

// em-multipliers of the resolved fontSize, mirroring NativeBase's `lineHeights`.
const lineHeightTokens = {
  '2xs': 1,
  xs: 1.125,
  sm: 1.25,
  md: 1.375,
  lg: 1.5,
  xl: 1.75,
  '2xl': 2,
  '3xl': 2.5,
  '4xl': 3,
  '5xl': 4,
} as const;
type LineHeightToken = keyof typeof lineHeightTokens;

export const fontHeadingStyles = StyleSheet.create({
  regular: { fontFamily: fontConfig.Flame[400].normal },
  bold: { fontFamily: fontConfig.Flame[700].normal },
});

export const fontBodyStyles = StyleSheet.create({
  regular: { fontFamily: fontConfig['Flame-Sans'][400].normal },
  bold: { fontFamily: fontConfig['Flame-Sans'][700].normal },
});

const fontFamilyMap: Record<string, typeof fontHeadingStyles> = {
  heading: fontHeadingStyles,
  body: fontBodyStyles,
};

// TODO refactor this to be static styles after nativebase Text removal
const stylesheetStyles = StyleSheet.create({
  base: {
    color: primitive.$bbqBrown,
    fontFamily: fontConfig['Flame-Sans'][400].normal,
  },
  ...Object.keys(textVariants).reduce((acc, key) => {
    const textVariantKey = key as TextVariant;
    const config = textVariants[textVariantKey];
    const style: TextStyle = {
      fontSize: getMobileValue(config.fontSize),
    };

    if ('lineHeight' in config) {
      style.lineHeight = getMobileValue(config.lineHeight);
    }

    if (config.fontFamily) {
      style.fontFamily = fontFamilyMap[config.fontFamily]?.regular?.fontFamily;
    }
    if ('textDecorationLine' in config) {
      style.textDecorationLine = config.textDecorationLine as TextStyle['textDecorationLine'];
    }

    // TODO refactor this to real styles after nativebase Text removal
    if ('mb' in config) {
      style.marginBottom = config.mb * 4;
    }
    if ('cursor' in config) {
      style.cursor = config.cursor as TextStyle['cursor'];
    }
    acc[textVariantKey] = style;
    return acc;
  }, {} as Record<string, TextStyle>),
  ...Object.keys(fontSizes).reduce((acc, key) => {
    const fontSizeKey = key as FontSizeToken;
    acc[fontSizeKey] = { fontSize: fontSizes[fontSizeKey] };
    return acc;
  }, {} as Record<string, TextStyle>),
});

function getMobileValue(value: number | number[]): number {
  return Array.isArray(value) ? value[0] : value;
}

// The rule tracks the text color, falling back to the same default `base` uses so an
// underlined Typography with no explicit color still matches its own glyphs.
function underlineStyle(color: string | undefined): TextStyle {
  return {
    borderBottomWidth: 1,
    borderBottomColor: color ?? primitive.$bbqBrown,
  };
}

function resolveFontSize(
  fontSize: TypographyProps['fontSize'],
  variant: TextVariant
): number | undefined {
  if (typeof fontSize === 'number') {
    return fontSize;
  }
  if (fontSize) {
    return fontSizes[fontSize];
  }
  const variantConfig = textVariants[variant];
  return variantConfig ? getMobileValue(variantConfig.fontSize) : undefined;
}

function resolveLineHeightStyle(
  lineHeight: TypographyProps['lineHeight'],
  lineHeightMultiplier: TypographyProps['lineHeightMultiplier'],
  fontSize: TypographyProps['fontSize'],
  variant: TextVariant
): TextStyle | undefined {
  // `lineHeight` wins over `lineHeightMultiplier` when both are set.
  if (lineHeight == null && lineHeightMultiplier == null) {
    return undefined;
  }
  if (typeof lineHeight === 'number') {
    return { lineHeight };
  }

  let multiplier: number | undefined;
  if (typeof lineHeight === 'string') {
    multiplier = lineHeight.endsWith('em')
      ? parseFloat(lineHeight)
      : lineHeightTokens[lineHeight as LineHeightToken];
  } else if (lineHeightMultiplier != null) {
    multiplier = lineHeightMultiplier;
  }
  if (multiplier == null || !Number.isFinite(multiplier)) {
    return undefined;
  }

  const resolvedFontSize = resolveFontSize(fontSize, variant);
  if (resolvedFontSize == null) {
    return undefined;
  }
  return { lineHeight: resolvedFontSize * multiplier };
}

// TODO refactor this with nativebase <Text/> removal
function addVariantProps(variant: TextVariant, incomingProps: TextProps): TextProps {
  const variantConfig = textVariants[variant];

  if (!variantConfig) {
    return incomingProps;
  }

  if ('accessibilityRole' in variantConfig && !incomingProps.accessibilityRole) {
    incomingProps.accessibilityRole = variantConfig.accessibilityRole as AccessibilityRole;
  }
  if ('accessibilityLevel' in variantConfig && !incomingProps.accessibilityLevel) {
    incomingProps.accessibilityLevel = variantConfig.accessibilityLevel as HeaderAccessibilityLevel;
  }
  if ('maxFontSizeMultiplier' in variantConfig && !incomingProps.maxFontSizeMultiplier) {
    incomingProps.maxFontSizeMultiplier = variantConfig.maxFontSizeMultiplier;
  }
  return incomingProps;
}
